import ClearIcon from '@material-design-icons/svg/outlined/clear.svg?react';
import CloseIcon from '@material-design-icons/svg/outlined/close.svg?react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { SearchablePopoverShell, type Choice } from '../SearchablePopoverShell';
import { captureAndReportError } from '../../utils/errorReporting';
export type { Choice } from '../SearchablePopoverShell';

interface Props<C extends Choice> {
  className?: string;
  choices: C[];
  defaultValues?: string[];
  inputValue?: string[];
  value?: string[]; // For controlled mode
  name?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  renderChoice?(choice: C): React.ReactNode;
  onChange?(values: string[]): void;
  onInvalid?(e: React.InvalidEvent<HTMLSelectElement>): void;
  disabled?: boolean;
  minSelections?: number;
  maxSelections?: number;
  maxVisibleSelections?: number;
  groupBy?(choice: C): string;
}

export function SearchableMultiSelect<C extends Choice>(props: Props<C>) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isChipListExpanded, setIsChipListExpanded] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const pendingSelectionEventRef = useRef(false);

  const isControlled = props.value !== undefined;
  const controlledSelectedKeys = isControlled
    ? new Set(props.value)
    : undefined;

  const [uncontrolledSelectedKeys, setUncontrolledSelectedKeys] = useState<
    Set<string>
  >(new Set(props.defaultValues || []));

  const selectedKeys = controlledSelectedKeys || uncontrolledSelectedKeys;
  const setSelectedKeys = isControlled
    ? (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
        const newSet =
          typeof updater === 'function' ? updater(selectedKeys) : updater;
        const newArray = Array.from(newSet);
        props.onChange?.(newArray);
      }
    : setUncontrolledSelectedKeys;

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (
      !isControlled &&
      !hasInitialized.current &&
      props.defaultValues &&
      props.defaultValues.length > 0
    ) {
      setUncontrolledSelectedKeys(new Set(props.defaultValues));
      hasInitialized.current = true;
    }
  }, [isControlled, props.defaultValues]);

  const selectedChoices = props.choices.filter((choice) =>
    selectedKeys.has(choice.key),
  );
  const maxVisibleSelections =
    props.maxVisibleSelections ?? selectedChoices.length;
  const hasOverflow = selectedChoices.length > maxVisibleSelections;
  const visibleSelectedChoices =
    hasOverflow && !isChipListExpanded
      ? selectedChoices.slice(0, maxVisibleSelections)
      : selectedChoices;
  const hiddenSelectedChoiceCount =
    selectedChoices.length - visibleSelectedChoices.length;
  const renderChoice = props.renderChoice || ((choice) => choice.label);
  const serializedInputValue = JSON.stringify(
    props.inputValue ?? Array.from(selectedKeys),
  );

  useEffect(() => {
    const hiddenInput = hiddenInputRef.current;

    if (!hiddenInput) {
      return;
    }

    if (!pendingSelectionEventRef.current) {
      return;
    }

    pendingSelectionEventRef.current = false;
    hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
  }, [serializedInputValue]);

  const queueSelectionEvents = () => {
    pendingSelectionEventRef.current = true;
  };

  const getFilteredChoices = (choices: C[], searchTerm: string) => {
    if (!searchTerm) return choices;

    const searchWords = searchTerm
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);

    return choices.filter((choice) => {
      const labelLower = choice.label.toLowerCase();
      const keyLower = choice.key.toLowerCase();
      const groupLower = props.groupBy?.(choice).toLowerCase() ?? '';

      return searchWords.every(
        (word) =>
          labelLower.includes(word) ||
          keyLower.includes(word) ||
          groupLower.includes(word),
      );
    });
  };

  const toggleSelection = (choiceKey: string) => {
    setSelectedKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(choiceKey)) {
        if (props.minSelections && newSet.size <= props.minSelections) {
          return prev;
        }
        newSet.delete(choiceKey);
      } else {
        if (props.maxSelections && newSet.size >= props.maxSelections) {
          return prev;
        }
        newSet.add(choiceKey);
      }

      const newArray = Array.from(newSet);
      queueSelectionEvents();

      if (!isControlled) {
        setTimeout(() => {
          props.onChange?.(newArray);
        }, 0);
      }

      return newSet;
    });
  };

  const removeSelection = (choiceKey: string) => {
    if (isProcessing) {
      return;
    }

    if (isControlled) {
      const currentSelection = props.value || [];

      if (
        props.minSelections &&
        currentSelection.length <= props.minSelections
      ) {
        return;
      }

      setIsProcessing(true);
      const newSelection = currentSelection.filter((key) => key !== choiceKey);
      queueSelectionEvents();
      props.onChange?.(newSelection);

      setTimeout(() => setIsProcessing(false), 100);
    } else {
      if (props.minSelections && selectedKeys.size <= props.minSelections) {
        return;
      }

      setIsProcessing(true);
      setSelectedKeys((prev) => {
        const newSet = new Set(prev);
        newSet.delete(choiceKey);
        const newArray = Array.from(newSet);
        queueSelectionEvents();

        setTimeout(() => {
          props.onChange?.(newArray);
          setIsProcessing(false);
        }, 100);

        return newSet;
      });
    }
  };

  const clearAll = () => {
    queueSelectionEvents();
    setSelectedKeys(new Set());
    if (!isControlled) {
      setTimeout(() => {
        props.onChange?.([]);
      }, 0);
    }
  };

  const displayText =
    selectedChoices.length === 0
      ? props.placeholder || 'Select'
      : selectedChoices.length === 1
        ? renderChoice(selectedChoices[0])
        : `${selectedChoices.length} selected`;

  return (
    <>
      {props.name && (
        <input
          ref={hiddenInputRef}
          type="hidden"
          name={props.name}
          value={serializedInputValue}
        />
      )}
      <div className="space-y-2">
        {selectedChoices.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {visibleSelectedChoices.map((choice) => (
              <div
                key={choice.key}
                className="inline-flex items-center gap-1 px-3 py-1 bg-neutral-600 text-white text-sm rounded-full border border-neutral-500"
              >
                <span>{renderChoice(choice)}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSelection(choice.key);
                  }}
                  className="p-0.5 hover:bg-neutral-500 rounded-full transition-colors"
                  disabled={
                    isProcessing ||
                    (!!props.minSelections &&
                      selectedKeys.size <= props.minSelections)
                  }
                >
                  <CloseIcon className="size-3 fill-current" />
                </button>
              </div>
            ))}
            {hiddenSelectedChoiceCount > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsChipListExpanded(true);
                }}
                className="inline-flex items-center px-3 py-1 bg-neutral-700 text-neutral-300 text-sm rounded-full border border-neutral-600 hover:bg-neutral-600 hover:text-white transition-colors cursor-pointer"
              >
                ... {hiddenSelectedChoiceCount} more
              </button>
            )}
            {hasOverflow && isChipListExpanded && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsChipListExpanded(false);
                }}
                className="inline-flex items-center px-3 py-1 bg-neutral-700 text-neutral-300 text-sm rounded-full border border-neutral-600 hover:bg-neutral-600 hover:text-white transition-colors cursor-pointer"
              >
                Show less
              </button>
            )}
            {selectedChoices.length > 1 &&
              (!props.minSelections || props.minSelections === 0) && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="inline-flex items-center gap-1 px-3 py-1 text-neutral-400 text-sm hover:text-white transition-colors"
                >
                  <ClearIcon className="size-4 fill-current" />
                  Clear all
                </button>
              )}
          </div>
        )}

        <SearchablePopoverShell
          className={twMerge(
            'bg-neutral-800 border-neutral-600 border rounded-md focus:border-teal-500 focus:ring-1 focus:ring-teal-500 hover:border-neutral-500 disabled:cursor-not-allowed disabled:opacity-50',
            props.className,
          )}
          disabled={props.disabled}
          choices={props.choices}
          searchPlaceholder={props.searchPlaceholder}
          triggerContent={
            <span
              className={selectedChoices.length === 0 ? 'text-neutral-400' : ''}
            >
              {displayText}
            </span>
          }
          getFilteredChoices={getFilteredChoices}
          getHighlightTerms={(searchTerm) =>
            searchTerm
              .toLowerCase()
              .trim()
              .split(/\s+/)
              .filter((word) => word.length > 0)
          }
          onHighlightError={(error, searchTerm, searchWords) => {
            captureAndReportError('Regex error in highlightMatch:', {
              searchTerm,
              searchWords,
              error,
            });
          }}
          onSelect={(choice) => {
            toggleSelection(choice.key);
          }}
        >
          {({
            filteredChoices,
            highlightedIndex,
            itemRefs,
            listRef,
            setHighlightedIndex,
            select,
            highlightMatch,
          }) => (
            <div ref={listRef} className="max-h-60 overflow-auto p-1">
              {filteredChoices.length === 0 ? (
                <div className="text-neutral-500 text-sm text-center py-4">
                  No results found
                </div>
              ) : (
                filteredChoices.map((choice, index) => {
                  const isSelected = selectedKeys.has(choice.key);
                  const groupLabel = props.groupBy?.(choice);
                  const showGroupHeader =
                    groupLabel !== undefined &&
                    (index === 0 ||
                      props.groupBy?.(filteredChoices[index - 1]) !==
                        groupLabel);
                  return (
                    <Fragment key={choice.key}>
                      {showGroupHeader && (
                        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                          {groupLabel}
                        </div>
                      )}
                      <div
                        ref={(el) => {
                          itemRefs.current[index] = el;
                        }}
                        onClick={() => select(choice)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className={[
                          'px-4 py-2 cursor-pointer rounded-lg text-sm flex items-center justify-between text-white',
                          'hover:bg-neutral-700',
                          index === highlightedIndex && 'bg-neutral-700',
                          isSelected && 'text-teal-400',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <span>{highlightMatch(choice.label)}</span>
                        {isSelected && <span className="text-xs">✓</span>}
                      </div>
                    </Fragment>
                  );
                })
              )}
            </div>
          )}
        </SearchablePopoverShell>
      </div>
    </>
  );
}
